/**
 * 회원 관리 서비스
 * - 관리자: 부동산/제휴업체 목록·상세 조회, 상태 변경
 */
import { db } from '../firebase/config';
import { collection, getDocs, getDoc, doc, updateDoc, query, where, orderBy, serverTimestamp } from 'firebase/firestore';

// ───── 유틸 ─────
const toDate = (ts) => {
  if (!ts) return null;
  let d;
  if (ts.toDate) d = ts.toDate();
  else if (ts.seconds) d = new Date(ts.seconds * 1000);
  else if (typeof ts === 'string') return ts;
  else return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// ───── 부동산(realtor) ─────

export const getAllRealtors = async () => {
  // orderBy 절은 해당 필드 없는 문서를 결과에서 제외하므로 client-side 정렬로 처리
  const q = query(collection(db, 'users'), where('role', '==', 'realtor'));
  const snap = await getDocs(q);
  const users = snap.docs.map((d) => {
    const raw = d.data();
    const created = raw.createdAt || raw.created_at;
    return { uid: d.id, ...raw, createdAt: toDate(created) };
  });

  // realtors 컬렉션 조인 — 병렬 조회 (N+1 완화)
  const rDocs = await Promise.all(
    users.map((u) => getDoc(doc(db, 'realtors', u.uid)).catch(() => null))
  );
  users.forEach((u, i) => {
    const rDoc = rDocs[i];
    if (rDoc && rDoc.exists()) {
      const { createdAt: _ignore, ...rest } = rDoc.data();
      Object.assign(u, rest);
    }
  });

  users.sort((a, b) => {
    if (!a.createdAt && !b.createdAt) return 0;
    if (!a.createdAt) return 1;
    if (!b.createdAt) return -1;
    return a.createdAt < b.createdAt ? 1 : -1;
  });

  return users;
};

export const getRealtorDetail = async (uid) => {
  const userDoc = await getDoc(doc(db, 'users', uid));
  if (!userDoc.exists()) return null;

  const data = { uid, ...userDoc.data(), createdAt: toDate(userDoc.data().createdAt) };

  try {
    const rDoc = await getDoc(doc(db, 'realtors', uid));
    if (rDoc.exists()) {
      const { createdAt: _ignore, ...rest } = rDoc.data();
      Object.assign(data, rest);
    }
  } catch { /* skip */ }

  return data;
};

// ───── 제휴업체(partner) ─────

export const getAllPartners = async () => {
  // orderBy 절은 해당 필드 없는 문서를 결과에서 제외하므로 client-side 정렬로 처리
  const q = query(collection(db, 'users'), where('role', '==', 'partner'));
  const snap = await getDocs(q);
  const users = snap.docs.map((d) => {
    const raw = d.data();
    const created = raw.createdAt || raw.created_at;
    return { uid: d.id, ...raw, createdAt: toDate(created) };
  });

  // partners 컬렉션 조인 — 병렬 조회 (N+1 완화)
  const pDocs = await Promise.all(
    users.map((u) => getDoc(doc(db, 'partners', u.uid)).catch(() => null))
  );
  users.forEach((u, i) => {
    const pDoc = pDocs[i];
    if (pDoc && pDoc.exists()) {
      const { createdAt: _ignore, ...rest } = pDoc.data();
      Object.assign(u, rest);
    }
  });

  // 최신순 정렬 (createdAt 없는 문서는 뒤로)
  users.sort((a, b) => {
    if (!a.createdAt && !b.createdAt) return 0;
    if (!a.createdAt) return 1;
    if (!b.createdAt) return -1;
    return a.createdAt < b.createdAt ? 1 : -1;
  });

  return users;
};

export const getPartnerDetail = async (uid) => {
  const userDoc = await getDoc(doc(db, 'users', uid));
  if (!userDoc.exists()) return null;

  const data = { uid, ...userDoc.data(), createdAt: toDate(userDoc.data().createdAt) };

  try {
    const pDoc = await getDoc(doc(db, 'partners', uid));
    if (pDoc.exists()) Object.assign(data, pDoc.data());
  } catch { /* skip */ }

  return data;
};

// ───── 상태 변경 ─────

export const updateUserStatus = async (uid, status) => {
  const update = { status };
  if (status === 'active') {
    const snap = await getDoc(doc(db, 'users', uid));
    if (snap.exists() && !snap.data().approved_at) {
      update.approved_at = serverTimestamp();
    }
  }
  await updateDoc(doc(db, 'users', uid), update);
};

// ───── 승인/거절 ─────

export const approveUser = async (uid) => {
  const snap = await getDoc(doc(db, 'users', uid));
  const update = { status: 'active' };
  if (snap.exists() && !snap.data().approved_at) {
    update.approved_at = serverTimestamp();
  }
  await updateDoc(doc(db, 'users', uid), update);
};

export const rejectUser = async (uid) => {
  await updateDoc(doc(db, 'users', uid), { status: 'suspended' });
};
